import { isClient } from '@vueuse/core'
import { acceptHMRUpdate, defineStore } from 'pinia'

interface productsData {
  item_name: string
  item_id: string
  price: number
  item_category: string
  item_category2: string
  item_category3: string
  item_list_name: string
  item_list_id: string
  index: number
  currency: string
  quantity: number
  affiliation: string
  completion_type: 'online' | 'lead' | 'partner-clickout'
}
interface trackEventData {
  event_action?: string
  event_label?: string
}
interface successEventData {
  event?: string
  step?: string
}
interface purchaseData {
  prodId?: string | number
  transaction_id?: string
  page_name?: string
  affiliation?: string
  value?: number
  currency?: string
  coupon?: string
  completion_type?: 'online' | 'lead' | 'partner-clickout'
}
interface addToCartEventData {
  prodId: string | number
  event: 'add_to_cart' | 'remove_from_cart'
  event_action: 'addon' | 'offer'
}

interface pageEventData {
  page_name?: string
  country_custom?: string
  p_product_family?: string
  p_product?: string
  cg1?: string
  cg2?: string
  cg3?: string
  cg4?: string
  cg5?: string
  uid?: string
  login_status?: string
  page_env?: string
  page_content_type?: string
  platform?: string
  event?: string
  event_type?: string
}

export function useAnalytics(data?: pageEventData) {
  if (isClient) {
    window.dataLayer ||= []
    if (sessionStorage.getItem('isFirstInteraction') === null) sessionStorage.setItem('isFirstInteraction', 'true')
    if (sessionStorage.getItem('isFirstCheckout') === null) sessionStorage.setItem('isFirstCheckout', 'true')
  }

  const appConfig = useAppConfig()
  const platform = appConfig.platformName
  const currency = appConfig.currency
  const storeDef = defineStore('ga4', {
    state: () => ({
      event_type: '',
      p_product: '',
      cg1: '',
      cg2: '',
      cg3: '',
      cg4: '',
      cg5: '',
      uid: '',
      page_name: '',
      p_product_family: '',
      event: '',
      ecommerce: {},
      event_category: '',
      login_status: '',
      currency,
      page_content_type: '',
      productList: [] as productsData[],
      addonsList: [] as productsData[],
    }),
    getters: {
    },
    actions: {
      pageEventTrack() {
        try {
          if (!isClient) return
          const productFamily = this.p_product_family && this.p_product_family !== 'uncategorized' ? `${this.p_product_family}/` : ''
          const product = this.p_product && this.p_product !== 'uncategorized' ? `${this.p_product}/` : ''
          const pageObject = {
            event: 'page_view',
            page_name: `${platform}/owned/${productFamily}${product}${this.page_name}`,
            country_custom: appConfig?.locale?.code === 'cs' ? 'cz' : appConfig?.locale?.code,
            p_product_family: this.p_product_family,
            p_product: this.p_product,
            cg1: this.cg1,
            cg2: this.p_product_family,
            cg3: this.cg3,
            cg4: this.cg4 || this.p_product,
            cg5: this.cg5,
            uid: this.uid,
            login_status: this.login_status || 'logged-out',
            page_env: import.meta.env.PROD ? 'prod' : 'dev',
            page_content_type: this.page_content_type || 'typeless',
            platform,
          }
          window.dataLayer?.push(pageObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      trackEvent(data: trackEventData) {
        try {
          const eventObject = {
            event: this.event,
            event_action: `form.${data.event_action}`,
            event_category: `form.${this.p_product}`,
            event_label: `form.${this.page_name}.${data.event_label?.toLowerCase()}`,
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: this.page_name,
          }
          if (sessionStorage.getItem('isFirstInteraction') === 'true' && this.event === 'calculator-form') {
            eventObject.event = 'calculator-start'
            eventObject.event_category = `form.${this.p_product}`
            sessionStorage.setItem('isFirstInteraction', 'false')
            window.dataLayer?.push(eventObject)
            this.trackEvent(data)
          }
          else if (sessionStorage.getItem('isFirstCheckout') === 'true' && this.event === 'checkout') {
            eventObject.event = 'begin-checkout'
            eventObject.event_category = this.p_product
            Object.assign(eventObject, this.ecommerce)
            window.dataLayer?.push(eventObject)
            sessionStorage.setItem('isFirstCheckout', 'false')
            this.trackEvent(data)
          }
          else if (this.event === 'checkout') {
            eventObject.event_category = this.p_product
            Object.assign(eventObject, this.ecommerce)
            window.dataLayer?.push(eventObject)
          }
          else {
            eventObject.event = this.event
            eventObject.event_category = `form.${this.p_product}`
            window.dataLayer?.push(eventObject)
          }
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      errorEvent(data: trackEventData) {
        try {
          const errorEventObject = {
            event: `${this.event}-error`,
            event_action: `form.error-${data.event_label?.toLowerCase()}`,
            event_category: `form.${this.p_product}`,
            event_label: 'form.error',
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: this.page_name,
          }
          window.dataLayer?.push(errorEventObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      successEvent(data?: successEventData) {
        try {
          const successEventObject = {
            event: data?.event || 'calculator-successful',
            event_action: `form.success-${this.p_product}`,
            event_category: `form.${this.p_product}`,
            event_label: 'form-success',
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: data?.step || 'full-application',
          }
          window.dataLayer?.push(successEventObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      buttonEvent(data: trackEventData) {
        try {
          const buttonEventObject = {
            event: `form.${data.event_action}`,
            event_action: `form.${data.event_action}`,
            event_category: `form.${this.p_product}`,
            event_label: `form.${this.page_name}.${data.event_label?.toLowerCase()}`,
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: this.page_name,
          }
          window.dataLayer?.push(buttonEventObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      actualProductList() {
        try {
          const actualProductListObject = {
            event: 'view_item_list',
            event_category: (this.productList.length > 0) ? 'item-list-successful' : 'item-list-failed',
            event_action: 'item-list-successful',
            event_label: this.p_product_family,
            amount_offers: this.productList.length,
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: this.page_name,
            ecommerce: {
              items: [...this.productList],
            },
          }
          if (this.page_name === 'upsell-list') {
            actualProductListObject.event_category = this.p_product
            actualProductListObject.event_action = 'addons'
            actualProductListObject.event_label = `${this.page_name}.view-addons`
            actualProductListObject.amount_offers = this.productList.length + this.addonsList.length
            actualProductListObject.ecommerce = {
              items: [...this.productList, ...this.addonsList],
            }
          }
          if (this.productList.length === 0 && this.page_name === 'upsell-list') return
          window.dataLayer?.push({ ecommerce: null }) // send empty ecommerce object
          window.dataLayer?.push(actualProductListObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      openDetailEvent(prodId: string) {
        try {
          const openDetailEventObject = {
            event: 'view_item',
            event_action: 'offers',
            event_category: this.p_product,
            event_label: 'offer-detail-page.view_item',
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: 'offer-detail-page',
            ecommerce: {
              items: [[...this.productList, ...this.addonsList].find(x => x.item_id === prodId)],
            },
          }
          window.dataLayer?.push(openDetailEventObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      addToCartEvent(data: addToCartEventData) {
        try {
          const addToCartEventObject = {
            event: data.event,
            event_action: data.event_action,
            event_label: `${this.page_name}.add-to-cart`,
            event_category: this.p_product,
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: 'add-2-cart-click',
            ecommerce: {
              items: [[...this.productList, ...this.addonsList].find(x => x.item_id === data.prodId)],
            },
          }
          window.dataLayer?.push({ ecommerce: null }) // send empty ecommerce object
          window.dataLayer?.push(addToCartEventObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      purchaseEvent(data: purchaseData) {
        try {
          const purchaseEventObject = {
            event: 'purchase',
            event_category: this.p_product,
            event_action: 'confirmation-page',
            event_label: this.p_product,
            event_type: this.event_type,
            platform,
            product: this.p_product,
            product_family: this.p_product_family,
            step: data.page_name || this.page_name,
            ecommerce: {
              transaction_id: data.transaction_id,
              affiliation: data.affiliation || 'owned',
              value: this.productList[0]?.price,
              currency: this.currency,
              coupon: data.coupon || '',
              completion_type: data.completion_type || 'online',
              items: [[...this.productList].find(x => x.item_id === data.prodId)],
            },
          }
          window.dataLayer?.push({ ecommerce: null }) // send empty ecommerce object
          window.dataLayer?.push(purchaseEventObject)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
      addCustomEvent(data: any) {
        try {
          window.dataLayer?.push(data)
        }
        catch (error) {
          console.error('GA4 action', error)
        }
      },
    },
  })

  if (import.meta.hot)
    import.meta.hot.accept(acceptHMRUpdate(storeDef, import.meta.hot))
  const store = storeDef()
  Object.assign(store.$state, data)
  return store
}
